import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAudit } from "../context/AuditContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  User, Briefcase, FolderOpen, ArrowRight, ShieldCheck,
  Building2, Layers, Search, Sparkles, Activity, Shield, KeyRound,
  CheckCircle2, Clock, Trash2, Loader2
} from "lucide-react";
import { PageLayout } from "../components/PageLayout";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  audit_manager: "Audit Manager",
  senior_auditor: "Senior Auditor",
  auditor: "Auditor",
  external_auditor: "External Auditor",
  control_owner: "Control Owner",
  observer: "Observer",
};

function isAssignedToRun(run, userId) {
  return (run?.assigned_users || []).some((item) => {
    if (typeof item === "string") return item === userId;
    return item?.user_id === userId;
  });
}

function formatAuditTimestamp(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { auditRuns, fetchRuns, loadRun, deleteRun } = useAudit();
  const [filterText, setFilterText] = useState("");
  const [deletingRunId, setDeletingRunId] = useState(null);

  const departmentValue =
    user?.department || (user?.role === "super_admin" ? "Enterprise Governance" : "Internal Audit");
  
  const processAreasList = useMemo(() => {
    if (Array.isArray(user?.process_areas) && user.process_areas.length > 0) {
      return user.process_areas;
    }
    if (user?.role === "super_admin") {
      return ["Platform Oversight", "Audit Operations", "Risk Monitoring", "Compliance Controls"];
    }
    return ["General Audit", "Risk & Compliance"];
  }, [user]);

  useEffect(() => {
    fetchRuns().catch((err) => {
      console.error("Failed to fetch runs:", err);
      toast.error("Failed to load audits. Please refresh or log in again.");
    });
  }, [fetchRuns]);

  const summary = useMemo(() => {
    const runs = auditRuns || [];
    const owned = runs.filter((run) => run.created_by === user?.id).length;
    const assigned = runs.filter((run) => isAssignedToRun(run, user?.id)).length;
    return {
      total: runs.length,
      owned,
      assigned,
    };
  }, [auditRuns, user?.id]);

  const visibleAuditsLabel = useMemo(() => {
    if (!user?.role) return "Accessible Audits";
    const role = ROLE_LABELS[user.role] || user.role;
    return `${role} Audits`;
  }, [user?.role]);

  const filteredAuditRuns = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return auditRuns;
    return auditRuns.filter((run) =>
      String(run.name || "").toLowerCase().includes(q) ||
      String(run.status || "").toLowerCase().includes(q)
    );
  }, [auditRuns, filterText]);

  const openAudit = async (run) => {
    try {
      const loaded = await loadRun(run.id);
      navigate(`/phase/${loaded?.current_phase || 1}`);
    } catch (error) {
      if (error?.response?.status === 401) {
        toast.error("Your session expired. Please sign in again.");
        logout();
        navigate("/login", { replace: true });
        return;
      }
      if (error?.response?.status === 403) {
        toast.error("You do not have access to this audit.");
        return;
      }
      toast.error(error?.response?.data?.detail || "Failed to open audit");
    }
  };

  const handleDeleteRun = async (run) => {
    setDeletingRunId(run.id);
    try {
      await deleteRun(run.id);
      toast.success(`Audit "${run.name}" deleted`);
    } catch (error) {
      if (error?.response?.status === 401) {
        toast.error("Your session expired. Please sign in again.");
        logout();
        navigate("/login", { replace: true });
        return;
      }
      if (error?.response?.status === 403) {
        toast.error("You do not have permission to delete audits.");
        return;
      }
      toast.error(error?.response?.data?.detail || "Failed to delete audit");
    } finally {
      setDeletingRunId(null);
    }
  };

  const userInitials = useMemo(() => {
    const raw = String(user?.name || user?.email || "A").trim();
    const parts = raw.split(/\s+/).filter(Boolean);
    const initials = parts.slice(0, 2).map((part) => part[0]).join("");
    return (initials || raw[0] || "A").toUpperCase();
  }, [user?.email, user?.name]);

  return (
    <PageLayout
      icon={User}
      title="User Profile & Workspace"
      subtitle="Manage your identity credentials, role permissions, and active audit assignments."
      data-testid="profile-page"
    >
      <div className="space-y-6">
        {/* HERO BANNER CARD */}
        <div className="relative overflow-hidden rounded-3xl border border-border/80 bg-gradient-to-r from-emerald-900/90 via-slate-900 to-teal-950 p-6 sm:p-8 text-white shadow-xl">
          {/* Subtle decorative glow orb */}
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl pointer-events-none" />
          <div className="absolute -left-20 -bottom-20 h-64 w-64 rounded-full bg-teal-500/20 blur-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-4 sm:gap-6">
              {/* Avatar Emblem */}
              <div className="relative shrink-0">
                <div className="flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-2xl bg-gradient-to-tr from-emerald-500 to-teal-400 text-2xl sm:text-3xl font-black text-white shadow-lg shadow-emerald-500/30 ring-4 ring-white/10">
                  {userInitials}
                </div>
                <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 ring-2 ring-slate-900" title="Active Account">
                  <CheckCircle2 className="h-3.5 w-3.5 text-white" />
                </span>
              </div>

              {/* User Info */}
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl sm:text-2xl font-black tracking-tight text-white font-heading">
                    {user?.name || "Unknown User"}
                  </h1>
                  <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-xs font-semibold px-2.5 py-0.5">
                    {ROLE_LABELS[user?.role] || user?.role || "Auditor"}
                  </Badge>
                </div>
                <p className="text-xs sm:text-sm text-emerald-200/80 font-medium">
                  {user?.email || "No email provided"}
                </p>
                <div className="flex items-center gap-3 pt-1 text-xs text-slate-300">
                  <span className="inline-flex items-center gap-1">
                    <Building2 className="w-3.5 h-3.5 text-emerald-400" />
                    {departmentValue}
                  </span>
                  <span>•</span>
                  <span className="inline-flex items-center gap-1">
                    <Shield className="w-3.5 h-3.5 text-emerald-400" />
                    Verified Access
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Metrics Badge Group */}
            <div className="flex items-center gap-3 bg-white/5 backdrop-blur-md border border-white/10 p-3 sm:p-4 rounded-2xl self-start md:self-auto">
              <div className="text-center px-3">
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-300 block">Total</span>
                <span className="text-xl font-extrabold text-white">{summary.total}</span>
              </div>
              <div className="h-8 w-px bg-white/10" />
              <div className="text-center px-3">
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-300 block">Owned</span>
                <span className="text-xl font-extrabold text-white">{summary.owned}</span>
              </div>
              <div className="h-8 w-px bg-white/10" />
              <div className="text-center px-3">
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-300 block">Assigned</span>
                <span className="text-xl font-extrabold text-white">{summary.assigned}</span>
              </div>
            </div>
          </div>
        </div>

        {/* CONTENT GRID */}
        <div className="grid gap-6 lg:grid-cols-12">
          {/* LEFT COLUMN: Account Details Card & Process Areas */}
          <div className="lg:col-span-4 space-y-6">
            <Card className="rounded-3xl border-border/80 shadow-sm overflow-hidden">
              <CardHeader className="bg-muted/40 pb-3 border-b border-border/60">
                <CardTitle className="text-sm font-bold flex items-center gap-2 text-foreground">
                  <KeyRound className="w-4 h-4 text-emerald-500" />
                  Account & Governance Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5 space-y-4 text-xs">
                <div className="flex items-center justify-between py-1 border-b border-border/40">
                  <span className="text-muted-foreground font-medium">System Role</span>
                  <Badge variant="outline" className="font-semibold text-emerald-600 dark:text-emerald-400 border-emerald-500/30 bg-emerald-500/10">
                    {ROLE_LABELS[user?.role] || user?.role || "Auditor"}
                  </Badge>
                </div>

                <div className="flex items-center justify-between py-1 border-b border-border/40">
                  <span className="text-muted-foreground font-medium">Primary Department</span>
                  <span className="font-semibold text-foreground">{departmentValue}</span>
                </div>

                <div className="flex items-center justify-between py-1 border-b border-border/40">
                  <span className="text-muted-foreground font-medium">Security Clearance</span>
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                    <ShieldCheck className="w-3.5 h-3.5" /> Level 3 Certified
                  </span>
                </div>

                <div className="space-y-2 pt-1">
                  <span className="text-muted-foreground font-medium flex items-center gap-1.5">
                    <Layers className="w-3.5 h-3.5 text-emerald-500" /> Process & Scope Areas
                  </span>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {processAreasList.map((area, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center rounded-lg bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300 border border-emerald-500/20"
                      >
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Platform Activity Card */}
            <Card className="rounded-3xl border-border/80 shadow-sm bg-gradient-to-br from-card via-card to-emerald-500/5">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                    <Activity className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-foreground">Continuous Compliance Active</h3>
                    <p className="text-[11px] text-muted-foreground">All audit streams synced in real-time</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* RIGHT COLUMN: Accessible Audit Runs */}
          <div className="lg:col-span-8 space-y-4">
            <Card className="rounded-3xl border-border/80 shadow-sm overflow-hidden">
              <CardHeader className="bg-muted/40 border-b border-border/60 py-4 px-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2">
                  <FolderOpen className="w-4 h-4 text-emerald-500" />
                  <CardTitle className="text-sm font-bold text-foreground">
                    {visibleAuditsLabel} ({filteredAuditRuns.length})
                  </CardTitle>
                </div>

                {/* Filter Search Input */}
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                  <Input
                    placeholder="Filter audits..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    className="h-8 pl-8 pr-3 text-xs rounded-xl bg-card border-border/80 focus-visible:ring-emerald-500/30"
                  />
                </div>
              </CardHeader>

              <CardContent className="p-6 space-y-3">
                {filteredAuditRuns.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-border p-10 text-center text-xs text-muted-foreground space-y-2">
                    <FolderOpen className="mx-auto h-8 w-8 text-muted-foreground/40" />
                    <p className="font-semibold text-foreground">No audit runs found</p>
                    <p>No audit runs match your filter or are assigned to your profile.</p>
                  </div>
                ) : (
                  filteredAuditRuns.map((run) => {
                    const owned = run.created_by === user?.id;
                    const assigned = isAssignedToRun(run, user?.id);
                    const phaseProgressPct = Math.round((Math.max(1, Math.min(10, run.current_phase || 1)) / 10) * 100);

                    return (
                      <div
                        key={run.id}
                        className="group flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl border border-border/70 p-4 transition-all duration-200 hover:border-emerald-500/40 hover:bg-emerald-500/[0.02] hover:shadow-md"
                      >
                        <div className="min-w-0 flex-1 space-y-1.5">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-bold text-sm text-foreground truncate group-hover:text-emerald-600 dark:group-hover:text-emerald-400">
                              {run.name}
                            </p>
                            {owned && (
                              <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 text-[10px] font-semibold gap-1">
                                <ShieldCheck className="w-3 h-3" />
                                Owner
                              </Badge>
                            )}
                            {assigned && !owned && (
                              <Badge variant="secondary" className="text-[10px] font-semibold gap-1">
                                <Briefcase className="w-3 h-3" />
                                Assigned
                              </Badge>
                            )}
                          </div>

                          {/* Audit Phase Progress Mini Bar */}
                          <div className="space-y-1 max-w-sm">
                            <div className="flex items-center justify-between text-[11px] text-muted-foreground font-medium">
                              <span>Phase {run.current_phase || 1} of 10</span>
                              <span className="font-mono text-emerald-600 dark:text-emerald-400 font-bold">{phaseProgressPct}%</span>
                            </div>
                            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                              <div
                                className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500"
                                style={{ width: `${phaseProgressPct}%` }}
                              />
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground pt-1">
                            <span className="inline-flex items-center gap-1">
                              <Clock className="w-3 h-3 text-emerald-500" />
                              Created: {formatAuditTimestamp(run.created_at)}
                            </span>
                            <span>•</span>
                            <span>Updated: {formatAuditTimestamp(run.updated_at)}</span>
                          </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={deletingRunId === run.id}
                                title="Delete audit run"
                                className="rounded-xl border-border/80 text-muted-foreground hover:border-rose-500/40 hover:bg-rose-500/10 hover:text-rose-600 dark:hover:text-rose-400 text-xs font-bold transition-all active:scale-95"
                              >
                                {deletingRunId === run.id ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Trash2 className="w-3.5 h-3.5" />
                                )}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete this audit run?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This permanently deletes "{run.name}" and all its documents,
                                  controls, and findings. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteRun(run)}
                                  className="bg-rose-600 text-white hover:bg-rose-700"
                                >
                                  Delete permanently
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>

                          <Button
                            size="sm"
                            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shrink-0 transition-all shadow-sm shadow-emerald-600/20 active:scale-95"
                            onClick={() => openAudit(run)}
                          >
                            Open Audit
                            <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

